import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import * as Icons from "@phosphor-icons/react";
import { PHISHING_INTRO, RED_FLAGS, SAFE_HABITS } from "../data/content";
import { API } from "../lib/api";

const fade = (i) => ({
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.4, delay: i * 0.05 },
});

export default function Learn() {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12 lg:py-20">
      {/* Hero */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-12 items-center">
        <motion.div {...fade(0)} className="md:col-span-7">
          <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">{PHISHING_INTRO.eyebrow}</p>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight text-foreground mt-3 leading-[1.02]">
            {PHISHING_INTRO.title}
          </h1>
          <p className="text-muted-foreground text-lg mt-6 leading-relaxed max-w-xl">{PHISHING_INTRO.lede}</p>
          <div className="flex flex-wrap items-center gap-3 mt-8">
            <Link
              to="/test"
              data-testid="learn-cta-test"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-primary text-white font-semibold hover:bg-primary/90 hover:-translate-y-0.5 transition-transform duration-200"
            >
              <Icons.Exam size={20} weight="duotone" /> Take the Test
            </Link>
            <a
              href={`${API}/guide/pdf`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="learn-download-pdf"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-full border-2 border-primary text-primary font-semibold bg-white hover:bg-primary/5 hover:-translate-y-0.5 transition-transform duration-200"
            >
              <Icons.FilePdf size={20} weight="duotone" /> Download full PDF guide
            </a>
          </div>
        </motion.div>
        <motion.div {...fade(1)} className="md:col-span-5">
          <div className="relative rounded-2xl overflow-hidden border border-border shadow-sm">
            <img
              src="https://images.unsplash.com/photo-1683117927786-f146451082fb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxlbWFpbCUyMHNlY3VyaXR5JTIwY29uY2VwdHxlbnwwfHx8fDE3ODc4NTUyMDl8MA&ixlib=rb-4.1.0&q=85"
              alt="Email security"
              className="w-full h-[280px] object-cover"
            />
          </div>
        </motion.div>
      </div>

      {/* Red flags */}
      <div className="mt-20">
        <motion.div {...fade(0)}>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-foreground">
            Six red flags to watch for
          </h2>
          <p className="text-muted-foreground mt-2">If you spot even one of these, slow down and double-check.</p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
          {RED_FLAGS.map((f, i) => {
            const Icon = Icons[f.icon] || Icons.Warning;
            return (
              <motion.div
                key={f.title}
                {...fade(i)}
                className="rounded-xl bg-white border border-border shadow-sm p-6 hover:-translate-y-1 hover:shadow-md transition-transform duration-200"
                data-testid={`red-flag-${i}`}
              >
                <div className="w-11 h-11 rounded-lg bg-muted flex items-center justify-center">
                  <Icon size={24} weight="duotone" className="text-accent" />
                </div>
                <h3 className="font-heading font-bold text-lg text-foreground mt-4">{f.title}</h3>
                <p className="text-muted-foreground text-sm mt-2 leading-relaxed">{f.text}</p>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Safe habits */}
      <div className="mt-20 grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-12">
        <motion.div {...fade(0)} className="md:col-span-4">
          <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
            <Icons.ShieldCheck size={26} weight="duotone" className="text-white" />
          </div>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-foreground mt-5">
            How to stay safe
          </h2>
          <p className="text-muted-foreground mt-3 leading-relaxed">
            A few simple habits stop almost every phishing attempt before it becomes a problem.
          </p>
        </motion.div>

        <div className="md:col-span-8 space-y-3">
          {SAFE_HABITS.map((h, i) => (
            <motion.div
              key={i}
              {...fade(i)}
              className="flex items-start gap-4 rounded-xl bg-white border border-border shadow-sm p-5"
              data-testid={`safe-habit-${i}`}
            >
              <div className="shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary font-heading font-bold flex items-center justify-center text-sm">
                {i + 1}
              </div>
              <p className="text-foreground leading-relaxed">{h}</p>
            </motion.div>
          ))}
        </div>
      </div>

      <motion.div
        {...fade(0)}
        className="mt-20 rounded-2xl bg-primary text-white p-8 sm:p-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6"
      >
        <div>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight">Ready to test your knowledge?</h2>
          <p className="text-white/70 mt-2">Try the interactive quiz — including real-looking phishing emails to spot.</p>
        </div>
        <Link
          to="/test"
          data-testid="learn-bottom-cta"
          className="shrink-0 inline-flex items-center gap-2 h-12 px-6 rounded-full bg-white text-primary font-semibold hover:-translate-y-0.5 transition-transform duration-200"
        >
          Start the Test <Icons.ArrowRight size={18} weight="bold" />
        </Link>
      </motion.div>
    </div>
  );
}
